import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import {
  Wallet2, TrendingUp, Clock, Banknote, ArrowUpRight, Music2, CheckCircle2,
  Download, Search, LayoutGrid, Disc3, Store, Receipt, Globe2, FileSpreadsheet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getRoyalties, getDistUser, getWithdrawals, getAnalytics, getReleases } from "@/lib/afmApi";
import { fmtUSD, fmtNum, fmtMonth } from "@/lib/format";
import { platformColor, platformId, platformGradient, platformGlow, flagEmoji, countryCode, toCSV, downloadCSV, buildBreakdown } from "@/lib/metrics";
import { exportXlsx } from "@/lib/xlsx";
import { SimuladoBadge } from "@/components/SimuladoBadge";
import { Loading, ErrorState } from "@/components/common";
import { WithdrawDialog } from "@/components/Withdraw";
import { BreakdownModal } from "@/components/BreakdownModal";

const TABS = [
  { id: "general", label: "Vista General", icon: LayoutGrid },
  { id: "tracks", label: "Tracks", icon: Music2 },
  { id: "products", label: "Productos", icon: Disc3 },
  { id: "stores", label: "Tiendas (DSPs)", icon: Store },
  { id: "transactions", label: "Tipos de Transacción", icon: Receipt },
  { id: "countries", label: "Países", icon: Globe2 },
];

const PERIODS = [
  { id: "12m", label: "Últimos 12 meses" },
  { id: "ytd", label: "Año en curso" },
  { id: "all", label: "Todo el historial" },
  { id: "custom", label: "Rango personalizado" },
];

const TX_TYPES = [
  { transaction_type: "subscription_audio", label: "Subscription Audio Streams", ratio: 0.62 },
  { transaction_type: "ad_supported_ugc", label: "Ad-Supported Video (UGC)", ratio: 0.14 },
  { transaction_type: "short_form_video", label: "Short-form Video Revenue", ratio: 0.12 },
  { transaction_type: "downloads", label: "Downloads", ratio: 0.07 },
  { transaction_type: "other", label: "Otros ingresos", ratio: 0.05 },
];

const PIE_COLORS = ["#9E1B22", "#171717", "#8A6F5C", "#C9A227", "#6B4E3D", "#A3785F", "#B9444C", "#3F3F46"];
const PIE_COLORS_DARK = ["#CA0808", "#F2F2F2", "#B08968", "#E3B93A", "#8C6A55", "#D9A98A", "#FF6B74", "#9CA3AF"];
const useIsDark = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

const STATUS_BADGE = {
  paid: { label: "Completado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  completed: { label: "Completado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  approved: { label: "En Proceso", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pending: { label: "En Proceso", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  processing: { label: "Retenido", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  rejected: { label: "Rechazado", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

// Column definitions (module level) — shared by table render and full-statement export.
const TRACK_COLS = [{ label: "Track", value: "title" }, { label: "ISRC", value: (r) => r.track_isrc || "—" }, { label: "Reproducciones", value: "stream_count" }, { label: "Ingresos USD", value: "revenue_usd" }];
const PRODUCT_COLS = [{ label: "Producto", value: "title" }, { label: "UPC", value: (r) => r.upc || "—" }, { label: "Tipo", value: "type" }, { label: "Fecha", value: "release_date" }, { label: "Reproducciones", value: "stream_count" }, { label: "Ingresos USD", value: (r) => (r.revenue_usd == null ? "" : r.revenue_usd) }];
const STORE_COLS = [{ label: "Tienda", value: "dsp_name" }, { label: "Reproducciones", value: (r) => r.stream_count ?? "" }, { label: "Ingresos USD", value: "revenue_usd" }, { label: "Participación %", value: "pct" }];
const TX_COLS = [{ label: "Tipo de transacción", value: "label" }, { label: "Reproducciones", value: "stream_count" }, { label: "Ingresos USD", value: "revenue_usd" }, { label: "Participación %", value: "pct" }];
const COUNTRY_COLS = [{ label: "País", value: "name" }, { label: "Código", value: "country_code" }, { label: "Reproducciones", value: "stream_count" }, { label: "Ingresos USD", value: "revenue_usd" }, { label: "Participación %", value: "pct" }];

const labelOf = (rel) => rel?.label || rel?.record_label || rel?.sello || "Mi Catálogo";

const EstTag = ({ show = true }) => (show ? <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">est.</span> : null);

function Sparkline({ data, id, color = "#9E1B22" }) {
  const chart = (data.length ? data : [1, 1]).map((v, i) => ({ i, v }));
  return (
    <div className="h-10 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chart} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.5} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${id})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const HeroCard = ({ testId, label, value, badge, sparkId, series, children }) => (
  <div data-testid={testId} className="rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] p-5">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#686166]">{label}</span>
      {badge && <span className="rounded-full border border-[#9E1B22]/30 bg-[#9E1B22]/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#9E1B22]">{badge}</span>}
    </div>
    <div className="mt-2 font-mono text-4xl font-extrabold tracking-tight text-[#9E1B22] sm:text-5xl">{value}</div>
    <div className="mt-2"><Sparkline data={series} id={sparkId} /></div>
    {children}
  </div>
);

const ExportBtn = ({ rows, columns, filename }) => (
  <div className="flex items-center gap-2">
    <button data-testid={`export-${filename}`} onClick={() => downloadCSV(`${filename}.csv`, toCSV(rows, columns))} disabled={!rows.length}
      className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-black disabled:opacity-40">
      <Download className="h-3.5 w-3.5" /> CSV
    </button>
    <button data-testid={`xlsx-${filename}`} onClick={() => exportXlsx(filename, [{ name: filename.replace("afm_", ""), columns, rows }])} disabled={!rows.length}
      className="flex items-center gap-1.5 rounded-lg border border-[#107C41]/40 bg-[#107C41]/5 px-3 py-1.5 text-xs font-semibold text-[#107C41] transition-colors hover:bg-[#107C41] hover:text-white disabled:opacity-40">
      <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
    </button>
  </div>
);

const Th = ({ children, right }) => <th className={`px-5 py-3 font-semibold ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono }) => <td className={`px-5 py-3 ${right ? "text-right" : ""} ${mono ? "font-mono tabular-nums" : ""}`}>{children}</td>;
const ClickableRow = ({ children, onClick, testId }) => (
  <tr data-testid={testId} onClick={onClick} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50">{children}</tr>
);

export default function Accounting() {
  const { user } = useAuth();
  const [roy, setRoy] = useState(null);
  const [dist, setDist] = useState(null);
  const [wd, setWd] = useState(null);
  const isDark = useIsDark();
  const pieColors = isDark ? PIE_COLORS_DARK : PIE_COLORS;
  const [analytics, setAnalytics] = useState(null);
  const [releases, setReleases] = useState([]);
  const [status, setStatus] = useState("loading");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [tab, setTab] = useState("general");
  const [period, setPeriod] = useState("12m");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [fLabel, setFLabel] = useState("all");
  const [fStore, setFStore] = useState("all");
  const [bd, setBd] = useState(null);

  const load = () => {
    setStatus("loading");
    Promise.all([
      getRoyalties(user.id),
      getDistUser(user.id).catch(() => null),
      getWithdrawals(user.id).catch(() => null),
      getAnalytics(user.id, "all").catch(() => null),
      getReleases(user.id).catch(() => []),
    ])
      .then(([r, d, w, a, rel]) => {
        setRoy(r); setDist(d); setWd(w); setAnalytics(a);
        setReleases(Array.isArray(rel) ? rel : rel?.releases || []);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  };
  useEffect(load, [user.id]);

  const net = roy?.summary?.net_artist_usd || 0;
  const platforms = useMemo(() => analytics?.platforms || [], [analytics]);
  const topTracks = useMemo(() => analytics?.top_tracks || [], [analytics]);
  const countries = useMemo(() => analytics?.countries || [], [analytics]);
  const totalTopStreams = useMemo(() => topTracks.reduce((a, t) => a + (t.streams || 0), 0) || 1, [topTracks]);
  const isrcByTitle = useMemo(() => {
    const m = {};
    releases.forEach((r) => { if (r.title) m[r.title.toLowerCase()] = r; });
    return m;
  }, [releases]);

  const periodSeries = useMemo(() => {
    let arr = (roy?.by_period || []).slice();
    const now = new Date();
    if (period === "12m") arr = arr.slice(0, 12);
    else if (period === "ytd") arr = arr.filter((p) => (p.period || "").startsWith(String(now.getFullYear())));
    else if (period === "custom" && (dateFrom || dateTo)) {
      const f = dateFrom ? dateFrom.slice(0, 7) : "0000-00";
      const t = dateTo ? dateTo.slice(0, 7) : "9999-99";
      arr = arr.filter((p) => (p.period || "") >= f && (p.period || "") <= t);
    }
    return arr.slice().reverse();
  }, [roy, period, dateFrom, dateTo]);
  const periodLabel = period === "custom"
    ? `Personalizado ${dateFrom || "…"} → ${dateTo || "…"}`
    : (PERIODS.find((p) => p.id === period)?.label || "");
  const sparkSeries = useMemo(() => periodSeries.map((p) => Number(p.net_usd) || 0).slice(-8), [periodSeries]);

  // ---------- datasets — prefer REAL backend arrays, fall back to proportional estimates ----------
  const trackRows = useMemo(() => {
    if (Array.isArray(roy?.by_track) && roy.by_track.length) {
      return roy.by_track.map((t) => ({ track_isrc: t.isrc || t.track_isrc || "", title: t.title || t.name || "", release_id: t.release_id || "", stream_count: t.stream_count ?? t.streams ?? 0, revenue_usd: Number(t.revenue_usd ?? t.net_usd ?? 0), label: t.label || "Mi Catálogo", estimated: false }));
    }
    return topTracks.map((t) => {
      const rel = isrcByTitle[(t.title || "").toLowerCase()];
      return { track_isrc: rel?.isrc || "", title: t.title, release_id: rel?.id || "", stream_count: t.streams || 0, revenue_usd: +((t.streams / totalTopStreams) * net).toFixed(2), label: labelOf(rel), estimated: true };
    });
  }, [roy, topTracks, isrcByTitle, totalTopStreams, net]);

  const productRows = useMemo(() => {
    if (Array.isArray(roy?.by_product) && roy.by_product.length) {
      return roy.by_product.map((p) => ({ upc: p.upc || "", title: p.title || p.name || "", type: p.type || "Producto", release_date: p.release_date || "", stream_count: p.stream_count ?? p.streams ?? 0, revenue_usd: Number(p.revenue_usd ?? p.net_usd ?? 0), label: p.label || "Mi Catálogo", estimated: false }));
    }
    return releases.map((r) => {
      const tt = topTracks.find((t) => (t.title || "").toLowerCase() === (r.title || "").toLowerCase());
      const streams = tt?.streams || 0;
      return { upc: r.upc || "", title: r.title, type: r.submission_type === "migration" ? "Migración" : "Single/Nuevo", release_date: r.release_date || "", stream_count: streams, revenue_usd: tt ? +((streams / totalTopStreams) * net).toFixed(2) : null, label: labelOf(r), estimated: true };
    });
  }, [roy, releases, topTracks, totalTopStreams, net]);

  const storeRows = useMemo(() => {
    const byStore = roy?.by_store || [];
    const totalRev = byStore.reduce((a, s) => a + (Number(s.net_usd) || 0), 0) || 1;
    return byStore.map((s) => {
      const plat = platforms.find((p) => (s.store || "").toLowerCase().startsWith((p.name || "").toLowerCase().split(" ")[0]));
      return { dsp_name: s.store, platform_id: platformId(s.store), stream_count: s.stream_count ?? plat?.streams ?? null, revenue_usd: Number(s.net_usd) || 0, pct: +(((Number(s.net_usd) || 0) / totalRev) * 100).toFixed(1), estimated: s.stream_count == null };
    }).sort((a, b) => b.revenue_usd - a.revenue_usd);
  }, [roy, platforms]);

  const txRows = useMemo(() => {
    if (Array.isArray(roy?.by_transaction) && roy.by_transaction.length) {
      const tot = roy.by_transaction.reduce((a, t) => a + Number(t.revenue_usd ?? t.net_usd ?? 0), 0) || 1;
      return roy.by_transaction.map((t) => ({ transaction_type: t.transaction_type || t.type || "", label: t.label || t.transaction_type || "", revenue_usd: Number(t.revenue_usd ?? t.net_usd ?? 0), stream_count: t.stream_count ?? t.streams ?? 0, pct: +((Number(t.revenue_usd ?? t.net_usd ?? 0) / tot) * 100).toFixed(1), estimated: false }));
    }
    const totalStreams = platforms.reduce((a, p) => a + (p.streams || 0), 0);
    return TX_TYPES.map((t) => ({ transaction_type: t.transaction_type, label: t.label, revenue_usd: +(net * t.ratio).toFixed(2), stream_count: Math.round(totalStreams * t.ratio), pct: +(t.ratio * 100).toFixed(1), estimated: true }));
  }, [roy, platforms, net]);

  const countryRows = useMemo(() => {
    if (Array.isArray(roy?.by_country) && roy.by_country.length) {
      return roy.by_country.map((c) => ({ country_code: c.country_code || countryCode(c.name) || "", name: c.name || c.country || "", stream_count: c.stream_count ?? c.streams ?? 0, pct: c.pct ?? 0, revenue_usd: Number(c.revenue_usd ?? c.net_usd ?? 0), estimated: false }));
    }
    return countries.map((c) => ({ country_code: countryCode(c.name) || "", name: c.name, stream_count: c.streams || 0, pct: c.pct || 0, revenue_usd: +((c.pct / 100) * net).toFixed(2), estimated: true }));
  }, [roy, countries, net]);

  // ---------- combined filters (sello / tienda) applied across sub-views ----------
  const labelOptions = useMemo(() => Array.from(new Set(releases.map((r) => labelOf(r)))).filter(Boolean), [releases]);
  const storeOptions = useMemo(() => storeRows.map((r) => r.dsp_name), [storeRows]);

  const trackRowsF = useMemo(() => (fLabel === "all" ? trackRows : trackRows.filter((r) => r.label === fLabel)), [trackRows, fLabel]);
  const productRowsF = useMemo(() => (fLabel === "all" ? productRows : productRows.filter((r) => r.label === fLabel)), [productRows, fLabel]);
  const storeRowsF = useMemo(() => (fStore === "all" ? storeRows : storeRows.filter((r) => r.dsp_name === fStore)), [storeRows, fStore]);

  const filtered = (rows, keys) => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(s)));
  };

  const withdrawRows = useMemo(() => (wd?.withdrawals || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [wd]);

  const openBreak = (kind, row) => {
    const pct = row.pct ?? (net ? +((row.revenue_usd / net) * 100).toFixed(1) : null);
    const tracks = kind === "track" ? [{ title: row.title, isrc: row.track_isrc, streams: row.stream_count }] : topTracks;
    setBd(buildBreakdown({ kind, id: row.id || row.track_isrc || row.upc || row.dsp_name || row.country_code || row.transaction_type || row.name, name: row.name || row.title || row.dsp_name || row.label, streams: row.stream_count, pct, revenue: row.revenue_usd, series: analytics?.series || [], topTracks: tracks }));
  };

  const statementSheets = () => {
    const s = roy.summary;
    const summaryRows = [
      { k: "Neto Artista (70%)", v: s.net_artist_usd }, { k: "Disponible para retiro", v: s.available_usd },
      { k: "Retenido / En proceso", v: s.locked_usd }, { k: "Pagado", v: s.paid_usd },
    ];
    const summaryCols = [{ label: "Concepto", value: "k" }, { label: "USD", value: "v" }];
    return [
      ["Resumen", summaryRows, summaryCols],
      ["Tracks", trackRowsF, TRACK_COLS],
      ["Productos", productRowsF, PRODUCT_COLS],
      ["Tiendas", storeRowsF, STORE_COLS],
      ["Transacciones", txRows, TX_COLS],
      ["Paises", countryRows, COUNTRY_COLS],
    ];
  };

  const exportStatement = () => {
    let csv = `Always Fresh Music — Estado de Cuenta\nArtista:,${user.name || user.email}\nPeriodo:,${periodLabel}\nSello:,${fLabel === "all" ? "Todos" : fLabel}\nTienda:,${fStore === "all" ? "Todas" : fStore}\n\n`;
    statementSheets().forEach(([title, rows, cols]) => { csv += `${title.toUpperCase()}\n${toCSV(rows, cols)}\n\n`; });
    downloadCSV(`afm_estado_cuenta_${period}.csv`, csv);
  };

  const exportStatementXlsx = () => {
    exportXlsx(`afm_estado_cuenta_${period}`, statementSheets().map(([name, rows, columns]) => ({ name, columns, rows })));
  };

  if (status === "loading") return <Loading />;
  if (status === "error") return <ErrorState onRetry={load} />;

  const s = roy.summary;
  const available = wd?.available_usd ?? s.available_usd;
  const min = Math.min(wd?.min_withdrawal_usd ?? 50, 50);
  const pendingCount = (wd?.withdrawals || []).filter((w) => ["pending", "approved"].includes(w.status)).length;
  const netPct = sparkSeries.length >= 2 && sparkSeries[sparkSeries.length - 2]
    ? ((sparkSeries[sparkSeries.length - 1] - sparkSeries[sparkSeries.length - 2]) / sparkSeries[sparkSeries.length - 2]) * 100 : null;

  return (
    <div className="space-y-8" data-testid="accounting-page">
      {/* Wallet hero */}
      <div className="relative overflow-hidden rounded-3xl border border-[#E5E5E5] bg-[#FAFAFA] p-6 md:p-8" data-testid="accounting-hero">
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-[#171717] sm:text-4xl">Contabilidad &amp; Regalías</h1>
            <p className="mt-2 max-w-xl font-editorial text-lg text-[#171717]">Audita tu catálogo a cualquier nivel · reparto 70% Artista / 30% SONA (neto, ya incluye tu Split Sheet).</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="export-statement" onClick={exportStatement} className="flex items-center gap-1.5 rounded-xl border border-[#9E1B22]/40 bg-white px-3 py-2.5 text-xs font-bold text-[#9E1B22] transition-colors hover:bg-[#9E1B22] hover:text-white">
              <FileSpreadsheet className="h-4 w-4" /> Estado (CSV)
            </button>
            <button data-testid="export-statement-xlsx" onClick={exportStatementXlsx} className="flex items-center gap-1.5 rounded-xl border border-[#107C41]/40 bg-white px-3 py-2.5 text-xs font-bold text-[#107C41] transition-colors hover:bg-[#107C41] hover:text-white">
              <FileSpreadsheet className="h-4 w-4" /> Estado (Excel)
            </button>
            <SimuladoBadge liveMode={dist?.live_mode ?? false} />
          </div>
        </div>
        <div className="relative mt-6 grid gap-4 md:grid-cols-3">
          <HeroCard testId="wallet-available" label="Disponible para Retiro" value={fmtUSD(available)} badge="Listo para transferir" sparkId="spark-available" series={sparkSeries}>
            <button data-testid="open-withdraw-button" onClick={() => setWithdrawOpen(true)} disabled={available < min}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#171717] px-4 py-2.5 text-sm font-bold text-[#FAFAFA] transition-all hover:bg-black active:scale-95 disabled:cursor-not-allowed disabled:opacity-60">
              <Wallet2 className="h-4 w-4" /> Retirar fondos
            </button>
            {available < min && <div className="mt-2 text-center text-[11px] text-[#686166]">Mínimo USD {min} para retirar</div>}
          </HeroCard>
          <HeroCard testId="wallet-net" label="Neto Acumulado Artista" value={fmtUSD(s.net_artist_usd)} badge="Total histórico" sparkId="spark-net" series={sparkSeries}>
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#171717]">
              {netPct != null ? (<><ArrowUpRight className={`h-3.5 w-3.5 ${netPct >= 0 ? "" : "rotate-90"}`} /> {netPct >= 0 ? "+" : ""}{netPct.toFixed(1)}% vs período anterior</>) : <><TrendingUp className="h-3.5 w-3.5" /> Tu participación (70%)</>}
            </div>
          </HeroCard>
          <HeroCard testId="wallet-locked" label="Retenido / En Proceso" value={fmtUSD(s.locked_usd)} badge="En verificación" sparkId="spark-locked" series={sparkSeries.map((v) => v * 0.25)}>
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#171717]"><Clock className="h-3.5 w-3.5" /> {pendingCount} {pendingCount === 1 ? "retiro en curso" : "retiros en curso"}</div>
          </HeroCard>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2" data-testid="analytics-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`analytics-tab-${t.id}`} onClick={() => { setTab(t.id); setQ(""); }}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Combined filter bar */}
      <div className="flex flex-wrap items-center gap-3" data-testid="analytics-filters">
        {tab !== "general" && (
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="analytics-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, ISRC, UPC…"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-black" />
          </div>
        )}
        <select data-testid="filter-label" value={fLabel} onChange={(e) => setFLabel(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black">
          <option value="all">Todos los sellos</option>
          {labelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="filter-store" value={fStore} onChange={(e) => setFStore(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black">
          <option value="all">Todas las tiendas</option>
          {storeOptions.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <select data-testid="analytics-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === "custom" && (
          <div className="flex items-center gap-2" data-testid="date-range">
            <input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black" />
            <span className="text-slate-400">→</span>
            <input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black" />
          </div>
        )}
      </div>

      {/* ---------------- TAB: GENERAL ---------------- */}
      {tab === "general" && (
        <div className="space-y-6" data-testid="tab-general">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="revenue-by-period">
              <h2 className="mb-4 font-serif text-lg font-bold text-slate-900">Ingresos por período</h2>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={periodSeries.map((p) => ({ period: fmtMonth(p.period), net: Number(p.net_usd) || 0 }))} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <defs><linearGradient id="revG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#CA0808" stopOpacity={0.3} /><stop offset="100%" stopColor="#CA0808" stopOpacity={0} /></linearGradient></defs>
                    <Tooltip formatter={(v) => [fmtUSD(v), "Neto"]} contentStyle={{ borderRadius: 10, border: isDark ? "1px solid #333" : "1px solid #e2e8f0", background: isDark ? "#141414" : "#fff", color: isDark ? "#fafafa" : "#111", fontSize: 12 }} itemStyle={{ color: isDark ? "#fafafa" : "#111" }} />
                    <Area type="monotone" dataKey="net" stroke="#CA0808" strokeWidth={2.5} fill="url(#revG)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="revenue-by-store-donut">
              <h2 className="mb-4 font-serif text-lg font-bold text-slate-900">Ingresos por tienda</h2>
              <div className="grid items-center gap-4 sm:grid-cols-[1fr_auto]">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={storeRowsF.map((r) => ({ name: r.dsp_name, value: r.revenue_usd }))} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2} stroke={isDark ? "#141414" : "#ffffff"} strokeWidth={2}>
                        {storeRowsF.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 10, border: isDark ? "1px solid #333" : "1px solid #e2e8f0", background: isDark ? "#141414" : "#fff", color: isDark ? "#fafafa" : "#111", fontSize: 12 }} itemStyle={{ color: isDark ? "#fafafa" : "#111" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-1" data-testid="revenue-by-store-legend">
                  {storeRowsF.slice(0, 8).map((r, i) => (
                    <li key={r.dsp_name} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20" style={{ background: pieColors[i % pieColors.length] }} />
                      <span className="truncate text-slate-700 dark:text-slate-200">{r.dsp_name}</span>
                      <span className="ml-auto font-mono font-semibold text-slate-900 dark:text-white">{fmtUSD(r.revenue_usd)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="transactions-table">
            <div className="border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Historial de Retiros</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>Fecha</Th><Th>Concepto</Th><Th>Método / Destino</Th><Th right>Monto</Th><Th right>Estado</Th></tr></thead>
                <tbody>
                  {withdrawRows.length ? withdrawRows.map((w, i) => {
                    const b = STATUS_BADGE[w.status] || { label: w.status || "—", cls: "bg-slate-100 text-slate-700 border-slate-200" };
                    const amount = w.amount_usd ?? w.amount ?? w.net_usd ?? 0;
                    return (
                      <tr key={w.id || i} data-testid={`transaction-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                        <Td mono><span className="text-xs text-slate-500">{w.created_at ? new Date(w.created_at).toLocaleDateString("es") : "—"}</span></Td>
                        <Td><span className="font-medium text-slate-800">Solicitud de retiro</span></Td>
                        <Td><span className="text-slate-600">{w.method || w.destination || "Retiro de saldo"}</span></Td>
                        <Td right mono><span className="font-semibold text-slate-900">{fmtUSD(amount)}</span></Td>
                        <td className="px-5 py-3 text-right"><span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${b.cls}`}>{b.label === "Completado" && <CheckCircle2 className="h-3 w-3" />}{b.label}</span></td>
                      </tr>
                    );
                  }) : <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400"><Banknote className="mx-auto mb-2 h-6 w-6 text-slate-300" />No hay retiros registrados aún.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- TAB: TRACKS ---------------- */}
      {tab === "tracks" && (() => {
        const rows = filtered(trackRowsF, ["title", "track_isrc"]);
        return (
          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="tab-tracks">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Analítica por canción <EstTag show={rows[0]?.estimated !== false} /></h2><ExportBtn rows={rows} columns={TRACK_COLS} filename="afm_tracks" /></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>Track</Th><Th>ISRC</Th><Th right>Reproducciones</Th><Th right>Ingresos</Th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((r, i) => (
                    <ClickableRow key={i} testId={`track-row-${i}`} onClick={() => openBreak("track", r)}>
                      <Td><span className="font-medium text-slate-800">{r.title}</span></Td>
                      <Td mono><span className="text-xs text-slate-500">{r.track_isrc || "—"}</span></Td>
                      <Td right mono><span className="text-slate-700">{fmtNum(r.stream_count)}</span></Td>
                      <Td right mono><span className="font-semibold text-slate-900">{fmtUSD(r.revenue_usd)}</span></Td>
                    </ClickableRow>
                  )) : <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-400">Sin resultados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ---------------- TAB: PRODUCTS ---------------- */}
      {tab === "products" && (() => {
        const rows = filtered(productRowsF, ["title", "upc"]);
        return (
          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="tab-products">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Productos (Álbumes / EP / Singles) <EstTag show={rows[0]?.estimated !== false} /></h2><ExportBtn rows={rows} columns={PRODUCT_COLS} filename="afm_products" /></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>Producto</Th><Th>UPC</Th><Th>Tipo</Th><Th>Fecha</Th><Th right>Reproducciones</Th><Th right>Ingresos</Th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((r, i) => (
                    <ClickableRow key={i} testId={`product-row-${i}`} onClick={() => openBreak("product", r)}>
                      <Td><span className="font-medium text-slate-800">{r.title}</span></Td>
                      <Td mono><span className="text-xs text-slate-500">{r.upc || "—"}</span></Td>
                      <Td><span className="text-slate-600">{r.type}</span></Td>
                      <Td mono><span className="text-xs text-slate-500">{r.release_date || "—"}</span></Td>
                      <Td right mono><span className="text-slate-700">{r.stream_count ? fmtNum(r.stream_count) : "—"}</span></Td>
                      <Td right mono><span className="font-semibold text-slate-900">{r.revenue_usd == null ? "—" : fmtUSD(r.revenue_usd)}</span></Td>
                    </ClickableRow>
                  )) : <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">Sin resultados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ---------------- TAB: STORES ---------------- */}
      {tab === "stores" && (() => {
        const rows = filtered(storeRowsF, ["dsp_name"]);
        const max = Math.max(...rows.map((r) => r.revenue_usd), 1);
        return (
          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="tab-stores">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Tiendas digitales (DSPs)</h2><ExportBtn rows={rows} columns={STORE_COLS} filename="afm_stores" /></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>Tienda</Th><Th right>Reproducciones</Th><Th right>Ingresos</Th><Th>Participación</Th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((r, i) => (
                    <ClickableRow key={i} testId={`store-row-${i}`} onClick={() => openBreak("store", r)}>
                      <Td><span className="flex items-center gap-2.5"><span className="h-6 w-6 shrink-0 rounded-md" style={{ backgroundColor: platformColor(r.dsp_name) }} /><span className="font-medium text-slate-800">{r.dsp_name}</span></span></Td>
                      <Td right mono><span className="text-slate-700">{r.stream_count != null ? fmtNum(r.stream_count) : "—"}</span></Td>
                      <Td right mono><span className="font-semibold text-slate-900">{fmtUSD(r.revenue_usd)}</span></Td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2.5 w-28 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${(r.revenue_usd / max) * 100}%`, background: platformGradient(r.dsp_name), boxShadow: platformGlow(r.dsp_name) }} /></div>
                          <span className="font-mono text-xs text-slate-500">{r.pct}%</span>
                        </div>
                      </td>
                    </ClickableRow>
                  )) : <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-400">Sin resultados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ---------------- TAB: TRANSACTIONS ---------------- */}
      {tab === "transactions" && (() => {
        const rows = filtered(txRows, ["label"]);
        return (
          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="tab-transactions">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Tipos de transacción <EstTag show={rows[0]?.estimated !== false} /></h2><ExportBtn rows={rows} columns={TX_COLS} filename="afm_transactions" /></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>Tipo</Th><Th right>Reproducciones</Th><Th right>Ingresos</Th><Th right>Participación</Th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <ClickableRow key={i} testId={`tx-row-${i}`} onClick={() => openBreak("transaction", r)}>
                      <Td><span className="font-medium text-slate-800">{r.label}</span></Td>
                      <Td right mono><span className="text-slate-700">{fmtNum(r.stream_count)}</span></Td>
                      <Td right mono><span className="font-semibold text-slate-900">{fmtUSD(r.revenue_usd)}</span></Td>
                      <Td right mono><span className="text-slate-500">{r.pct}%</span></Td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ---------------- TAB: COUNTRIES ---------------- */}
      {tab === "countries" && (() => {
        const rows = filtered(countryRows, ["name", "country_code"]);
        return (
          <div className="rounded-2xl border border-slate-100 bg-white" data-testid="tab-countries">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-lg font-bold text-slate-900">Desglose por país <EstTag show={rows[0]?.estimated !== false} /></h2><ExportBtn rows={rows} columns={COUNTRY_COLS} filename="afm_countries" /></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><Th>País</Th><Th right>Reproducciones</Th><Th right>Ingresos</Th><Th right>Participación</Th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((r, i) => (
                    <ClickableRow key={i} testId={`country-row-${i}`} onClick={() => openBreak("country", r)}>
                      <Td><span className="flex items-center gap-2"><span className="text-base leading-none">{flagEmoji(r.name)}</span><span className="font-medium text-slate-800">{r.name}</span><span className="font-mono text-[10px] text-slate-400">{r.country_code}</span></span></Td>
                      <Td right mono><span className="text-slate-700">{fmtNum(r.stream_count)}</span></Td>
                      <Td right mono><span className="font-semibold text-slate-900">{fmtUSD(r.revenue_usd)}</span></Td>
                      <Td right mono><span className="text-slate-500">{r.pct}%</span></Td>
                    </ClickableRow>
                  )) : <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-400">Sin resultados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <BreakdownModal open={!!bd} data={bd} onClose={() => setBd(null)} />
      <WithdrawDialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} userId={user.id} available={available} min={min} onDone={load} />
    </div>
  );
}
